import React from 'react';
import { Box, Stack } from '@mui/material';
import { Button } from '../Buttons/Button';
import { WebchatMessage, MessageContainer, MessageBubble } from '../WebchatMessage';

export interface BaseActivityMessageProps {
  /**
   * Whether to show skip button
   */
  allowSkip?: boolean;
  /**
   * Skip button text
   */
  skipText?: string;
  /**
   * Submit button text
   */
  submitText?: string;
  /**
   * Callback when skip button is clicked
   */
  onSkip?: () => void;
  /**
   * Whether the inputs are disabled
   */
  disabled?: boolean;
  /**
   * Whether the submit button should be enabled
   */
  submitEnabled?: boolean;
  /**
   * Hide the submit button entirely. Used when rendering a locked / read-only activity
   * (e.g. in conversation history where the user has already submitted a value).
   */
  hideSubmit?: boolean;
  /**
   * Message content to display above the input
   */
  children?: React.ReactNode;
  /**
   * Sender type: 'bot' or 'user'
   */
  sender: 'bot' | 'user';
  /**
   * Avatar URL for the sender
   */
  avatarUrl?: string;
  /**
   * Timestamp for the message
   */
  timestamp?: string;
  /**
   * Render function for the input component
   */
  renderInput: () => React.ReactNode;
  /**
   * Callback when submit button is clicked
   */
  onSubmit: () => void;
  /**
   * Optional form wrapper - if true, wraps in form element
   */
  useForm?: boolean;
  /**
   * Optional form submit handler - required if useForm is true
   */
  onFormSubmit?: (e: React.FormEvent) => void;
  /**
   * Custom bot display name passed to WebchatMessage
   */
  botName?: string;
  /**
   * Optional content rendered below the prompt bubble, outside its border/background
   * (e.g. an AI-translation disclosure hint). Forwarded to WebchatMessage's `footer` prop.
   */
  footer?: React.ReactNode;
}

/**
 * BaseActivityMessage provides a common structure for all activity message components.
 * It handles the message layout, button rendering, and common props, while allowing
 * child components to customize the input area via the renderInput prop. Supports a
 * hideSubmit prop for locked / read-only rendering (used in conversation history).
 */
export const BaseActivityMessage: React.FC<BaseActivityMessageProps> = ({
  allowSkip = false,
  skipText = 'Skip',
  submitText = 'Send',
  onSkip,
  disabled = false,
  submitEnabled = true,
  hideSubmit = false,
  children,
  sender,
  avatarUrl,
  timestamp,
  renderInput,
  onSubmit,
  useForm = false,
  onFormSubmit,
  botName,
  footer,
}) => {
  const content = (
    <>
      {children && (
        <WebchatMessage sender={sender} avatarUrl={avatarUrl} timestamp={timestamp} botName={botName} footer={footer}>
          {children}
        </WebchatMessage>
      )}
      <Box sx={{ ml: 9 }}>
        <MessageContainer sender={sender}>
          <MessageBubble sender={sender}>
            <Box sx={{ p: 2 }}>{renderInput()}</Box>
          </MessageBubble>
        </MessageContainer>
      </Box>
      <MessageContainer sender={sender}>
        <Stack
          direction="row"
          justifyContent="space-between"
          sx={{
            ml: 9,
          }}
        >
          {allowSkip && (
            <Button variant="text" onClick={onSkip} disabled={disabled} sx={{ mr: '10px' }}>
              {skipText}
            </Button>
          )}
          {sender === 'bot' && !hideSubmit && (
            <Button
              variant="contained"
              onClick={useForm ? undefined : onSubmit}
              type={useForm ? 'submit' : 'button'}
              disabled={!submitEnabled || disabled}
            >
              {submitText}
            </Button>
          )}
        </Stack>
      </MessageContainer>
    </>
  );

  if (useForm && onFormSubmit) {
    return <form onSubmit={onFormSubmit}>{content}</form>;
  }

  return content;
};
